<template>
  <div class="ci-Wrapper">
    <div v-if="!showOnlyReplies" :id="`ci-CommentItem-${item.id}`" class="ci-CommentItem">
      <ContributeLine
        v-if="isShowContributeLine"
        :class="{
          'ci-CommentItem_ContributeLine-withReplyForm': replyFormOpen && !isChildCommentsExist,
          'ci-CommentItem_ContributeLine-isReplyComment': isReplyComment
        }"
        class="ci-CommentItem_ContributeLine"
      />
      <AppAvatar :avatar-url="item.userAvatarUrl" class="ci-Avatar" />
      <div
        ref="mainContentRef"
        :class="{
          'ci-MainContent-with-checkin': Boolean(item.checkinData)
        }"
        class="ci-MainContent"
      >
        <div class="ci-InfoAndActionsWrapper">
          <div class="ci-Name" data-testid="user-name">
            {{ item.userName }}
          </div>
          <div class="ci-Time" data-testid="create-date">
            {{ calculateCommentTime(item.createDate) }}
          </div>
          <div
            v-if="item.editDate"
            v-tippy="{
              content: editInfo,
              placement: 'top'
            }"
            class="ci-Edit"
          >
            {{ $t('comments.edited') }}
          </div>
          <div class="ci-Actions" data-testid="all-actions">
            <CommentReplyButton
              v-if="!hideReplyButton"
              class="ci-ReplyBtn"
              @click="onReplyBtnClick(null)"
            />

            <DropdownMenu
              v-if="!hideActions && !isEmpty(menuActions)"
              :items="menuActions"
              :offset="[0, 0]"
              :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
              data-testid="dropdown-menu"
              @item-click="onMenuActionsClick($event, item.id)"
            >
              <template #activator>
                <AppButton class="ci-ReplyBtn" icon="more-next" size="sm" type="subtle" />
              </template>
            </DropdownMenu>
          </div>
        </div>

        <CommentItemCheckinPreview
          v-if="item.checkinData"
          :checkin-data="item.checkinData"
          :okr-element="objective"
          class="ci-CheckinPreview"
        />

        <DescriptionField
          v-if="isCommentShown"
          :class="{
            'ci-CheckinMessage': Boolean(item.checkinData)
          }"
          :model-value="item.text"
          :show-placeholder="false"
          :styled="false"
          :table-actions-append-to
          :workspace-id="objective.workspaceId"
          readonly
          simple-view
        />

        <AppReactions
          v-if="!isEdit"
          :comment-id="item.id"
          :emoji-picker-append-to="emojiPickerAppendTo"
          :reactions="item.reactions"
          :readonly="readonlyReactions"
          class="ci-Reactions"
          @update-reactions="
            $emit('update-reactions', {
              parentId: item.parentId,
              commentId: item.id,
              reactions: $event
            })
          "
        />

        <div
          v-if="!isReplyComment && getOtherChildCount > 0"
          class="ci-MoreReplies"
          data-testid="more-replies"
        >
          {{ $t('comments.more_reply', getOtherChildCount) }}
        </div>

        <CommentForm
          v-if="isEdit"
          :append-mention-to-body="appendMentionToBody"
          :auto-scroll-to-form-mode="autoScrollToFormMode"
          :class="{
            'ci-CheckinCommentForm': Boolean(item.checkinData)
          }"
          :hidden-features="hiddenFeatures"
          :model-value="item"
          :my-avatar="userData.userAvatarUrl"
          :objective="objective"
          :table-actions-append-to
          :tracking-source="trackingSource"
          active
          data-testid="comment-form"
          tracking-mode="main"
          @update="$emit('update', $event)"
          @update:active="isEdit = $event"
        />
      </div>
    </div>

    <div
      v-if="isChildCommentsExist || replyFormOpen"
      :style="{
        '--replies-list-gap':
          isShowOtherRepliesButtonNeeded && !showFullRepliesList
            ? 'var(--other-replies-button-offset-y, 12px)'
            : undefined
      }"
      class="ci-Replies"
    >
      <CommentList
        v-if="isChildCommentsExist"
        :auto-scroll-to-form-mode
        :can-be-replied="false"
        :emoji-picker-append-to="emojiPickerAppendTo"
        :hide-actions="hideActions"
        :hide-reply-button="hideReplyButton"
        :items="repliesList"
        :objective="objective"
        :parent-reply-form-open="replyFormOpen"
        :readonly-reactions="readonlyReactions"
        :show-replies-connections="showRepliesConnections"
        :table-actions-append-to
        :user-data="userData"
        data-testid="child-comments"
        is-reply-comment
        @remove="$emit('remove', $event)"
        @reply="onReplyBtnClick"
        @update="$emit('update-reply', $event)"
        @update-reactions="$emit('update-reactions', $event)"
      />

      <AppButton
        v-if="isShowOtherRepliesButtonNeeded"
        class="ci-ShowRepliesButton"
        size="sm"
        type="link-secondary"
        @click="showOtherReplies"
      >
        <div class="ci-ShowRepliesButton_Content">
          <AppIcon class="ci-ShowReplies_Icon" height="24" icon-name="show-replies" width="24" />
        </div>
        {{ showFullRepliesListButtonText }}
      </AppButton>

      <CommentForm
        v-if="replyFormOpen"
        ref="commentForm"
        :append-mention-to-body="appendMentionToBody"
        :auto-scroll-to-form-mode="autoScrollToFormMode"
        :hidden-features="hiddenFeatures"
        :my-avatar="userData.userAvatarUrl"
        :objective="objective"
        :reply-to="item.id"
        :table-actions-append-to
        :tracking-source="trackingSource"
        active
        data-testid="reply-form"
        tracking-mode="reply"
        @create="$emit('create-reply', $event)"
        @update="$emit('update-reply', $event)"
        @update:active="setReplyFormOpen"
      />
    </div>
  </div>
</template>

<script>
import { has, isEmpty } from 'lodash'
import { defineComponent } from 'vue'
import { mapState } from 'vuex'

import CommentsApiHandler from '@/api/comments'
import { TRACKING_UNKNOWN } from '@/tracking/amplitude-helpers'
import { validateRepliesToShowCount } from '@/utils/comments'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { getMonthList, getTimeDiffs } from '@/utils/date'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { includesValue } from '@/utils/prop-validators'
import { ELEMENT_VISIBILITY_CHECK_MODE } from '@/utils/window'

import ContributeLine from '@/components/objectives/forms/ContributeLine'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import CommentForm from '@/components/objectives/forms/objective-form/CommentForm'
import CommentItemCheckinPreview from '@/components/objectives/forms/objective-form/CommentItemCheckinPreview'
import CommentList from '@/components/objectives/forms/objective-form/CommentList'
import CommentReplyButton from '@/components/objectives/forms/objective-form/CommentReplyButton'
import AppReactions from '@/components/reactions/AppReactions'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'

export default defineComponent({
  name: 'CommentItem',
  components: {
    AppReactions,
    CommentItemCheckinPreview,
    AppIcon,
    CommentList,
    CommentReplyButton,
    DescriptionField,
    AppAvatar,
    AppButton,
    DropdownMenu,
    CommentForm,
    ContributeLine
  },

  props: {
    item: {
      type: Object,
      required: true
    },

    userData: {
      type: Object,
      required: true
    },

    objective: {
      type: Object,
      required: true
    },

    canBeReplied: {
      type: Boolean,
      default: false
    },

    hideActions: {
      type: Boolean
    },

    hideReplyButton: {
      type: Boolean
    },

    isReplyComment: {
      type: Boolean
    },

    isLast: {
      type: Boolean
    },

    parentReplyFormOpen: {
      type: Boolean
    },

    trackingSource: {
      type: String,
      default: TRACKING_UNKNOWN
    },

    hiddenFeatures: {
      type: Array,
      default: () => []
    },

    appendMentionToBody: {
      type: Boolean,
      default: true
    },

    showOnlyReplies: {
      type: Boolean
    },

    repliesToShow: {
      type: Number,
      default: Infinity,
      validator: v => validateRepliesToShowCount(v)
    },

    showRepliesConnections: {
      type: Boolean,
      default: true
    },

    autoScrollToFormMode: {
      type: String,
      default: ELEMENT_VISIBILITY_CHECK_MODE.SCROLLABLE_PARENT,
      validator: v => includesValue(v, ELEMENT_VISIBILITY_CHECK_MODE)
    },

    tableActionsAppendTo: {
      type: String,
      default: ''
    },

    emojiPickerAppendTo: {
      type: String,
      default: ''
    },

    readonlyReactions: {
      type: Boolean
    }
  },

  emits: {
    update: null,
    'update-reply': null,
    remove: null,
    'create-reply': null,
    reply: null,
    'reply-form-open': null,
    'reply-form-close': null,
    'update-reactions': null
  },

  data() {
    return {
      isEdit: false,
      replyFormOpen: false,
      editableCommentId: null,
      resizeObserver: null,
      mainContentHeight: '0px',
      showFullRepliesList: false
    }
  },

  computed: {
    DROPDOWN_MENU_TYPES: () => DROPDOWN_MENU_TYPES,

    ...mapState('pluginOptions', {
      isPluginAdmin: state => state.isPluginAdmin
    }),

    isShowOtherRepliesButtonNeeded() {
      return (
        Number.isFinite(this.repliesToShow) &&
        this.isChildCommentsExist &&
        this.item.childComments.length > this.repliesToShow &&
        !this.showFullRepliesList
      )
    },

    showFullRepliesListButtonText() {
      if (this.isShowOtherRepliesButtonNeeded) {
        const hiddenRepliesCount = this.item.childComments.length - this.repliesToShow

        return this.$t('action.load_more_replies', hiddenRepliesCount)
      }

      return this.$t('action.load_more')
    },

    repliesList() {
      if (this.isShowOtherRepliesButtonNeeded) {
        return this.showFullRepliesList
          ? this.item.childComments
          : this.item.childComments.slice(0, this.repliesToShow)
      }

      return this.item.childComments
    },

    isCommentShown() {
      if (!this.isEdit) {
        return !isStringEmpty(this.item.text)
      }

      return false
    },

    getOtherChildCount() {
      return this.item.childCount - 1
    },

    isShowContributeLine() {
      const {
        isLast,
        isReplyComment,
        isChildCommentsExist,
        replyFormOpen,
        parentReplyFormOpen,
        showRepliesConnections
      } = this

      if (!showRepliesConnections) return false

      // If this comment is the last comment, and the parent has a reply form open, we show the contribute line.
      const showContributeLineIfLastCommentAndParentHasReplyFormOpen =
        !isLast && (isChildCommentsExist || replyFormOpen || isReplyComment)

      // If this comment has child comments or a reply form open, we show the contribute line.
      const showContributeLineIfCommentHasChildCommentsOrReplyFormOpen =
        (isLast && parentReplyFormOpen) || isChildCommentsExist || replyFormOpen

      return (
        showContributeLineIfLastCommentAndParentHasReplyFormOpen ||
        showContributeLineIfCommentHasChildCommentsOrReplyFormOpen
      )
    },

    isChildCommentsExist() {
      return has(this.item, 'childComments') && !isEmpty(this.item.childComments)
    },

    editInfo() {
      return `${this.calculateCommentTime(this.item.editDate)} by ${this.item.userName}`
    },

    menuActions() {
      const result = []
      if (this.userData.userAccountId === this.item.accountId) {
        result.push({
          name: 'edit',
          title: 'action.edit',
          icon: 'edit-next',
          group: MENU_ITEMS_GROUPS.EDITING
        })
      }

      const isAuthor = this.userData.userAccountId === this.item.accountId

      if ((this.isPluginAdmin || isAuthor) && !this.item.checkinData) {
        result.push({
          name: 'remove',
          title: 'action.delete',
          icon: 'delete-next',
          group: MENU_ITEMS_GROUPS.REMOVING,
          color: 'var(--grade-low-color-next)'
        })
      }

      return result
    }
  },

  watch: {
    replyFormOpen(value) {
      if (value) {
        this.$emit('reply-form-open')
      } else {
        this.$emit('reply-form-close')
      }
    }
  },

  mounted() {
    this.initResizeObserver()
  },

  beforeUnmount() {
    this.disconnectObserver()
  },

  methods: {
    isEmpty,
    setReplyFormOpen(value) {
      this.replyFormOpen = value
    },

    async initResizeObserver() {
      await this.$nextTick()
      const { mainContentRef } = this.$refs
      if (!mainContentRef) return
      this.resizeObserver = new ResizeObserver(() => {
        this.mainContentHeight = mainContentRef.getBoundingClientRect().height + 'px'
      })

      this.resizeObserver.observe(mainContentRef)
    },

    disconnectObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },

    calculateCommentTime(item) {
      const createdOn = new Date(item)
      const now = new Date()

      const { diffForDay, diffForHour, diffForMin } = getTimeDiffs(now, createdOn)

      const formatHours = `0${createdOn.getHours()}`.slice(-2)
      const formatMins = `0${createdOn.getMinutes()}`.slice(-2)

      if (diffForDay >= 365) {
        return `${
          getMonthList()[createdOn.getMonth()]
        } ${createdOn.getDate()}, ${createdOn.getFullYear()} ${formatHours}:${formatMins}`
      }

      if (diffForDay > 7) {
        return `${
          getMonthList()[createdOn.getMonth()]
        } ${createdOn.getDate()}, ${formatHours}:${formatMins}`
      }

      if (diffForDay >= 1) {
        return `${
          getMonthList()[createdOn.getMonth()]
        } ${createdOn.getDate()}, ${createdOn.getFullYear()} ${formatHours}:${formatMins}`
      }

      if (diffForHour >= 1 && diffForDay < 1) {
        return `${diffForHour} ${this.$t('objective.hours_ago', diffForHour)}`
      }

      if (diffForHour < 1 && diffForMin >= 1) {
        return `${diffForMin} ${this.$t('token.minutes_ago')}`
      }

      if (diffForMin < 1) {
        return this.$t('common.just_now').toLowerCase()
      }

      return `${diffForDay} ${this.$t('objectives.days_ago')}`
    },

    onMenuActionsClick(name, editableCommentId) {
      if (name === 'remove') {
        this.remove()
      } else {
        this.edit(editableCommentId)
      }
    },

    async remove() {
      try {
        const commentsApi = new CommentsApiHandler()
        await commentsApi.removeComment({
          commentId: this.item.id,
          remove: true
        })
        this.$emit('remove', this.item)
      } catch (error) {
        handleError({ error })
      }
    },

    edit(editableCommentId) {
      this.isEdit = true
      this.editableCommentId = editableCommentId
    },

    async onReplyBtnClick(payload) {
      this.showOtherReplies()
      await this.$nextTick()
      if (this.canBeReplied === false) {
        this.$emit('reply', this.item)
      } else {
        this.openReplyForm(payload)
      }
    },

    showOtherReplies() {
      if (!this.showFullRepliesList) {
        this.showFullRepliesList = true
      }
    },

    /**
     * @public
     */
    async openReplyForm(payload) {
      this.showOtherReplies()
      await this.$nextTick()
      this.setReplyFormOpen(true)
      const commentPayload = payload || this.item

      this.$nextTick(() => {
        this.$refs.commentForm.replyOnComment(commentPayload)
      })
    },

    /**
     * @public
     */
    closeReplyForm() {
      this.setReplyFormOpen(false)
    },

    /**
     * @public
     */
    focusReplyForm() {
      if (this.$refs.commentForm) {
        this.$refs.commentForm.focus()
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.ci-Wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--comment-item-wrapper-gap, 24px);
}
.ci-CommentItem {
  display: flex;
  position: relative;
}

.ci-MainContent {
  flex: 1 1 auto;
  font-family: $system-ui;
  font-style: normal;
  display: flex;
  flex-direction: column;

  &:not(&-with-checkin) {
    gap: 8px;
  }

  &-with-checkin {
    gap: 4px;
  }
}

.ci-Reactions {
  .ci-MainContent-with-checkin & {
    margin-top: 4px;
  }
}

.ci-InfoAndActionsWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 24px;
}

.ci-Name {
  font-weight: fw('semi-bold');
  color: $primary-color-next;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ci-Avatar {
  align-self: flex-start;
  position: sticky;
  top: 0;
  z-index: 1;
}

.ci-Time,
.ci-Edit {
  font-style: normal;
  font-weight: fw('regular');
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-3;
  flex: 0 0 auto;
}

//.ci-Message {
//  line-height: 24px;
//}
.ci-Actions {
  display: flex;
  visibility: hidden;
  gap: 16px;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;

  .ci-CommentItem:hover & {
    visibility: visible;
  }
  &:deep(.o-droplist--expanded) {
    .ci-ReplyBtn {
      visibility: visible;
    }
  }
}

// .ci-EmojiMenu {
//   margin-right: 4px;
// }

//.ci-Message {
//  width: 100%;
//  word-break: break-word;
//  font-weight: fw('regular');
//  font-size: $fs-14;
//  line-height: 20px;
//  color: $dark-1;
//}

.ci-ReplyBtn {
  color: $dark-2;
  visibility: hidden;

  .ci-CommentItem:hover & {
    visibility: visible;
  }
  &:hover {
    color: $primary-color-next;
  }
}

.ci-Replies {
  margin-inline: var(--replies-list-mx, 32px 0);
  margin-block: var(--replies-list-my, 0);
  //margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: var(--replies-list-gap, 24px);
  --comment-form-padding: 0;
  &:deep(.cf-CommentForm) {
    z-index: 2;
  }
}

.ci-CommentItem_ContributeLine {
  position: absolute;
  left: 11px;
  top: 33px;
  --height: calc(100% + 4px);
  //--height: 100%;
  z-index: 1;
  &-isReplyComment {
    left: -21px;
    --height: calc(100% + 34px);
    top: 5px;
  }
  &-withReplyForm {
    --height: calc(100% + 12px);
  }
}
.ci-MoreReplies {
  font-size: $fs-14;
  font-weight: fw('regular');
  line-height: 20px;
  color: $dark-3;
  margin-top: 2px;
}

.ci-CheckinMessage {
  --description-field-margin-bottom: 0;
  margin-top: 12px;
}

.ci-CheckinCommentForm {
  --comment-form-padding: 0;
}

.ci-CheckinPreview {
  margin-top: 4px;
}

.ci-ShowRepliesButton {
  height: 32px;
  align-self: flex-start;
  transition: color $transition-fast;

  &_Content {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  @media (any-hover: hover) {
    &:hover {
      color: $primary-color-next;
    }
  }
}

.ci-ShowReplies_Icon {
  border-radius: $border-radius-sm-next;
  transition: color $transition-fast ease-in-out, background-color $transition-fast ease-in-out;

  &:not(.ci-ShowRepliesButton:hover &) {
    color: $dark-2;
  }

  .ci-ShowRepliesButton:hover & {
    background-color: $grey-3-next;
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/mixins';

.ci-CommentItem {
  .o-droplist-standalone {
    min-height: 16px;
  }

  .o-textarea-field {
    width: calc(100% - 52px);
  }

  //.ci-Message * + p {
  //  margin-bottom: 0;
  //  margin-top: 0.75rem;
  //}
  //.ci-Message p {
  //  margin-bottom: 0;
  //}
  //
  //.ci-Message span.mention {
  //  @extend %mention-item;
  //}
  //
  //.ci-Message ol {
  //  padding-left: 18px;
  //  margin: 0;
  //}
  //
  //.ci-Message ul {
  //  padding-left: 18px;
  //  margin-bottom: 0;
  //}
}
</style>
